"use client"

import React, { createContext, useContext, useState, useRef, useLayoutEffect } from "react"
import { createPortal } from "react-dom"
import { cn } from "../../utils/tailwindUtils.js"

const TooltipContext = createContext()

const TooltipProvider = ({ children }) => {
  return <>{children}</>
}

const Tooltip = ({ children }) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const closeTimer = useRef(null)

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const openTooltip = () => {
    cancelClose()
    setOpen(true)
  }

  const closeTooltip = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), 80)
  }

  React.useEffect(() => () => cancelClose(), [])

  return (
    <TooltipContext.Provider value={{ open, setOpen, triggerRef, openTooltip, closeTooltip }}>
      {children}
    </TooltipContext.Provider>
  )
}

const TooltipTrigger = ({ children }) => {
  const { openTooltip, closeTooltip, triggerRef } = useContext(TooltipContext)

  return (
    <span
      ref={triggerRef}
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
      onFocus={openTooltip}
      onBlur={closeTooltip}
    >
      {children}
    </span>
  )
}

const tooltipPosition = (rect, side, sideOffset) => {
  switch (side) {
    case "top":
      return { top: rect.top - sideOffset, left: rect.left + rect.width / 2 }
    case "bottom":
      return { top: rect.bottom + sideOffset, left: rect.left + rect.width / 2 }
    case "left":
      return { top: rect.top + rect.height / 2, left: rect.left - sideOffset }
    case "right":
      return { top: rect.top + rect.height / 2, left: rect.right + sideOffset }
    default:
      return { top: rect.top - sideOffset, left: rect.left + rect.width / 2 }
  }
}

const TooltipContent = React.forwardRef(
  ({ className, side = "top", sideOffset = 4, hidden, transition = false, children, maxWidth = "300px", textWrap = "wrap" }, ref) => {

    const wrapClass = {
      wrap: "whitespace-normal break-words",
      nowrap: "whitespace-nowrap",
      balance: "whitespace-normal text-balance break-words",
      pretty: "whitespace-normal text-pretty break-words"
    }[textWrap];

    const { open, triggerRef } = useContext(TooltipContext)
    const [style, setStyle] = useState(null)

    useLayoutEffect(() => {
      if (!open || hidden || !triggerRef.current) {
        return
      }
      const rect = triggerRef.current.getBoundingClientRect()
      const pos = tooltipPosition(rect, side, sideOffset)
      const parsed = parseFloat(maxWidth)
      const max = Number.isNaN(parsed)
        ? 320
        : String(maxWidth).includes("rem")
          ? parsed * 16
          : parsed
      const pad = 12
      let left = pos.left
      if (side === "top" || side === "bottom") {
        left = Math.min(Math.max(left, max / 2 + pad), window.innerWidth - max / 2 - pad)
      }
      let top = pos.top
      if (side === "top") {
        top = Math.max(pad, rect.top - sideOffset)
      }
      setStyle({
        position: "fixed",
        top,
        left,
        transform:
          side === "top"
            ? "translate(-50%, -100%)"
            : side === "bottom"
              ? "translateX(-50%)"
              : "translateY(-50%)",
        pointerEvents: "none",
      })
    }, [open, hidden, side, sideOffset, triggerRef, maxWidth])

    if (!open || hidden || !style || typeof document === "undefined") return null

    return createPortal(
      <div
        ref={ref}
        style={{
          ...style,
          maxWidth: maxWidth
        }}
        className={cn(
          "z-[9999] rounded-md border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md pointer-events-none",
          wrapClass,
          transition ? "animate-in fade-in-0 zoom-in-95" : "",
          className
        )}
      >
        {children}
      </div>,
      document.body,
    )
  }
)


TooltipContent.displayName = "TooltipContent"

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
